import {
  Add,
  ApiOutlined,
  CheckCircleOutline,
  DeleteOutline,
  EditOutlined,
  ExpandMore,
  StorageOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ConexaoCard from "./conexaoCard";
import useStyles from "./styles";

const POSICOES_POR_CATEGORIA = 10;

// Textos e ícones de cada categoria do catálogo (o catálogo em si vem do backend).
const CATEGORIAS_UI = {
  BANCO: {
    icone: StorageOutlined,
    singular: "Banco",
    vazio: "Nenhum banco de dados cadastrado para este ERP.",
    descricao: "Acesso direto ao banco de dados do ERP",
  },
  API: {
    icone: ApiOutlined,
    singular: "API",
    vazio: "Nenhuma API cadastrada para este ERP.",
    descricao: "Endereço e credenciais da API de integração",
  },
};

const iniciais = (nome = "") =>
  nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "ERP";

export default function ErpCard({
  erp,
  catalogo,
  ativoNoSistema,
  defaultExpanded,
  onEditar,
  onExcluir,
  onAdicionarConexao,
  onEditarConexao,
  onExcluirConexao,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const categorias = Object.entries(catalogo?.categorias || {});

  const acao = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={styles.card}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={styles.summary}>
        <Box sx={styles.summaryContent}>
          <Avatar variant="rounded" sx={styles.avatar}>
            {iniciais(erp.nome)}
          </Avatar>
          <Box sx={styles.summaryText}>
            <Box sx={styles.nomeRow}>
              <Typography sx={styles.nome}>{erp.nome}</Typography>
              <Chip label={erp.tipo} size="small" sx={styles.chipTipo} />
              {ativoNoSistema ? (
                <Tooltip title="ERP utilizado pelo sistema. O cadastro tem prioridade sobre a variável de ambiente ERP.">
                  <Chip
                    icon={<CheckCircleOutline />}
                    label="ERP ativo no sistema"
                    size="small"
                    sx={styles.chipAtivo}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Os dados deste ERP ficam salvos, mas este tipo de ERP não é utilizado pelo sistema.">
                  <Chip
                    label="Não utilizado pelo servidor"
                    size="small"
                    variant="outlined"
                    sx={styles.chipInativo}
                  />
                </Tooltip>
              )}
            </Box>
            {erp.descricao && (
              <Typography sx={styles.descricao}>{erp.descricao}</Typography>
            )}
          </Box>

          <Box sx={styles.summaryResumo}>
            {categorias.map(([categoria, def]) => {
              const Icone = CATEGORIAS_UI[categoria]?.icone || StorageOutlined;
              const total = erp.configs.filter(
                (c) => c.categoria === categoria,
              ).length;
              return (
                <Tooltip key={categoria} title={`Conexões de ${def.titulo}`}>
                  <Box sx={styles.resumoItem}>
                    <Icone sx={styles.resumoIcone} />
                    <Typography sx={styles.resumoTexto}>{total}</Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          <Box sx={styles.summaryActions}>
            <Tooltip title="Editar ERP">
              <IconButton size="small" onClick={acao(onEditar)}>
                <EditOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir ERP">
              <IconButton size="small" color="error" onClick={acao(onExcluir)}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={styles.details}>
        {categorias.length === 0 && (
          <Typography sx={styles.vazioTexto}>
            Ainda não há configurações disponíveis para o tipo {erp.tipo}.
          </Typography>
        )}

        {categorias.map(([categoria, def]) => {
          const ui = CATEGORIAS_UI[categoria] || {};
          const Icone = ui.icone || StorageOutlined;
          const conexoes = erp.configs.filter((c) => c.categoria === categoria);
          const lotado = conexoes.length >= POSICOES_POR_CATEGORIA;

          return (
            <Box key={categoria} sx={styles.secao}>
              <Box sx={styles.secaoHeader}>
                <Box sx={styles.secaoIconeBox}>
                  <Icone sx={styles.secaoIcone} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={styles.secaoTituloRow}>
                    <Typography sx={styles.secaoTitulo}>
                      {def.titulo}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${conexoes.length}/${POSICOES_POR_CATEGORIA}`}
                      sx={styles.chipContador}
                    />
                  </Box>
                  {ui.descricao && (
                    <Typography sx={styles.secaoDescricao}>
                      {ui.descricao}
                    </Typography>
                  )}
                </Box>
                <Tooltip
                  title={
                    lotado
                      ? `Limite de ${POSICOES_POR_CATEGORIA} conexões atingido`
                      : ""
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Add />}
                      disabled={lotado}
                      onClick={() => onAdicionarConexao(categoria)}
                    >
                      Adicionar {ui.singular || def.titulo}
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              {conexoes.length === 0 ? (
                <Box sx={styles.vazio}>
                  <Typography sx={styles.vazioTexto}>
                    {ui.vazio || "Nenhuma conexão cadastrada para este ERP."}
                  </Typography>
                </Box>
              ) : (
                <Box sx={styles.grid}>
                  {conexoes.map((conexao) => (
                    <ConexaoCard
                      key={conexao.id}
                      conexao={conexao}
                      campos={def.campos}
                      nomePadrao={`${ui.singular || def.titulo} ${conexao.posicao}`}
                      onEditar={() => onEditarConexao(conexao)}
                      onExcluir={() => onExcluirConexao(conexao)}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}
